import {
  ChakraBox,
  ChakraFlex,
  FAIconName,
  FireflyButton,
  ModalV2,
  MultiSelectOption,
  Text,
  UniversalSelect,
} from '@equip.health/ui';
import { useEffect, useMemo, useState } from 'react';

import { CarePlanInterventionResponse } from '~/lib/types/carePlan/carePlans';
import { useVoidCustomInterventionMutation } from '~/store/services/carePlan';
import { useGetInterventionVoidReasonsQuery } from '~/store/services/refCarePlanEnums';
import useErrorToast from '~/lib/hooks/useErrorToast';
import { GlobalErrorCode } from '~/lib/constants/global.constants';

const TOP_LEVEL_ID = 'void-intervention-modal';

type Props = {
  intervention: CarePlanInterventionResponse;
  isOpen: boolean;
  onClose: () => void;
  onVoided?: () => void;
  treatmentEpisodeId: string;
};

export const VoidInterventionModal = ({
  intervention,
  isOpen,
  onClose,
  onVoided,
  treatmentEpisodeId,
}: Props) => {
  const { showErrorToast } = useErrorToast();

  const {
    data: voidReasons = [],
    isLoading: isReasonsLoading,
    isError: isReasonsError,
  } = useGetInterventionVoidReasonsQuery(undefined, { skip: !isOpen });

  const [voidCustomIntervention, { isLoading: isVoiding }] =
    useVoidCustomInterventionMutation();

  const [selectedReasonCode, setSelectedReasonCode] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (!isOpen) {
      setSelectedReasonCode(null);
    }
  }, [isOpen]);

  const options: MultiSelectOption[] = useMemo(
    () =>
      voidReasons.map((reason) => ({
        label: reason.label,
        value: reason.code,
      })),
    [voidReasons],
  );

  const selectedOption = options.find((o) => o.value === selectedReasonCode);

  const isConfirmDisabled =
    !selectedReasonCode || isReasonsLoading || isReasonsError || isVoiding;

  const handleConfirm = async () => {
    if (!selectedReasonCode) return;
    try {
      await voidCustomIntervention({
        interventionId: intervention.id,
        treatmentEpisodeId,
        voidReasonCode: selectedReasonCode,
      }).unwrap();
      onVoided?.();
      onClose();
    } catch (error) {
      showErrorToast(
        GlobalErrorCode.VOID_CUSTOM_INTERVENTION,
        error.status,
        error,
      );
    }
  };

  return (
    <ModalV2
      bodyOverflow="visible"
      id={TOP_LEVEL_ID}
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      title="Void intervention"
    >
      <ChakraBox marginBottom="16px">
        <Text>
          Once voided, this intervention will no longer appear on the
          patient&rsquo;s care plan. This action cannot be undone.
        </Text>
      </ChakraBox>
      <ChakraBox marginBottom="24px">
        <Text marginBottom="6px" variant="caption">
          Reason for voiding
        </Text>
        <UniversalSelect
          ariaLabel="Reason for voiding"
          id={`${TOP_LEVEL_ID}__reason-select`}
          isDisabled={isReasonsLoading || isReasonsError}
          isSingleSelect
          modalId={TOP_LEVEL_ID}
          onChange={(
            newOption: MultiSelectOption | MultiSelectOption[] | null,
          ) => {
            const next = Array.isArray(newOption) ? newOption[0] : newOption;
            setSelectedReasonCode(next?.value ?? null);
          }}
          options={options}
          placeholder="Select one"
          value={selectedOption ?? null}
        />
      </ChakraBox>
      <ChakraFlex justifyContent="space-between" width="100%">
        <FireflyButton
          id={`${TOP_LEVEL_ID}__cancel`}
          leftIcon={FAIconName.timesRegular}
          onClick={onClose}
          variant="secondary"
        >
          Cancel
        </FireflyButton>
        <FireflyButton
          id={`${TOP_LEVEL_ID}__confirm`}
          isDisabled={isConfirmDisabled}
          isLoading={isVoiding}
          leftIcon={FAIconName.trashRegular}
          onClick={handleConfirm}
        >
          Void intervention
        </FireflyButton>
      </ChakraFlex>
    </ModalV2>
  );
};
